import { useCallback, useEffect, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
} from "lightweight-charts";

const DARK = {
  background: "#1E293B",
  text: "#CBD5E1",
  grid: "#334155",
  muted: "#94A3B8",
  up: "#22C55E",
  down: "#EF4444",
};

const LIGHT = {
  background: "#F8FAFC",
  text: "#1F2937",
  grid: "#D8DEE9",
  muted: "#64748B",
  up: "#16A34A",
  down: "#DC2626",
};

const OHLC_FIELDS = ["open", "high", "low", "close"];
const FX_CURRENCIES = new Set([
  "AUD",
  "CAD",
  "CHF",
  "EUR",
  "GBP",
  "JPY",
  "NZD",
  "USD",
]);
const MAX_PRICE_PRECISION = 8;

function decimalPlaces(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  const rounded = numeric.toFixed(MAX_PRICE_PRECISION).replace(/0+$/, "").replace(/\.$/, "");
  return Math.min(MAX_PRICE_PRECISION, (rounded.split(".")[1] || "").length);
}

function standardFxPrecision(symbol) {
  const clean = String(symbol || "")
    .split(":")
    .pop()
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();
  if (clean.length !== 6) return null;

  const base = clean.slice(0, 3);
  const quote = clean.slice(3, 6);
  if (!FX_CURRENCIES.has(base) || !FX_CURRENCIES.has(quote)) return null;
  return quote === "JPY" ? 3 : 5;
}

function inferPricePrecision(candles, symbol) {
  const fxPrecision = standardFxPrecision(symbol);
  let maxDecimals = 0;
  let referencePrice = null;

  for (const candle of candles || []) {
    for (const field of OHLC_FIELDS) {
      const value = candle?.[field];
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) continue;
      referencePrice = numeric;
      maxDecimals = Math.max(maxDecimals, decimalPlaces(value));
    }
  }

  if (Number.isFinite(fxPrecision)) return fxPrecision;
  if (maxDecimals > 0) return maxDecimals;
  if (!Number.isFinite(referencePrice)) return 2;
  const absolutePrice = Math.abs(referencePrice);
  if (absolutePrice < 0.1) return 6;
  if (absolutePrice < 1) return 5;
  if (absolutePrice < 10) return 4;
  return 2;
}

function buildPriceFormat(candles, symbol) {
  const precision = Math.max(0, Math.min(MAX_PRICE_PRECISION, inferPricePrecision(candles, symbol)));
  return {
    type: "price",
    precision,
    minMove: Math.pow(10, -precision),
  };
}

function priceScaleMinimumWidth(precision) {
  return Math.max(64, Math.min(96, 42 + (Number(precision) || 0) * 8));
}

function rightWhitespaceBars(candleCount) {
  return Math.max(4, Math.min(8, Math.ceil((Number(candleCount) || 0) * 0.04)));
}

function sanitizePayload(payload) {
  const width = Math.max(320, Math.min(Number(payload?.width) || 800, 2400));
  const height = Math.max(240, Math.min(Number(payload?.height) || 600, 1600));
  const candles = Array.isArray(payload?.candles) ? payload.candles : [];
  return {
    ...payload,
    width,
    height,
    theme: payload?.theme === "light" ? "light" : "dark",
    candles,
    indicator_series: Array.isArray(payload?.indicator_series) ? payload.indicator_series : [],
    indicator_names: Array.isArray(payload?.indicator_names) ? payload.indicator_names : [],
    show_volume: payload?.show_volume !== false,
  };
}

export default function ChartSnapshotRenderer() {
  const rootRef = useRef(null);
  const chartHostRef = useRef(null);
  const chartRef = useRef(null);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  const clearChart = useCallback(() => {
    try {
      chartRef.current?.remove();
    } catch {
      void 0;
    }
    chartRef.current = null;
    if (chartHostRef.current) {
      chartHostRef.current.innerHTML = "";
    }
  }, []);

  const renderPayload = useCallback((incomingPayload) => {
    const nextPayload = sanitizePayload(incomingPayload);
    setPayload(nextPayload);
    setError("");
    window.__PAXIA_CHART_READY__ = false;
    window.__PAXIA_CHART_ERROR__ = "";

    requestAnimationFrame(() => {
      try {
        if (!chartHostRef.current) throw new Error("chart host unavailable");
        if (!nextPayload.candles.length) throw new Error("payload has no candles");
        clearChart();

        const palette = nextPayload.theme === "light" ? LIGHT : DARK;
        const priceFormat = buildPriceFormat(nextPayload.candles, nextPayload.symbol);
        const rightOffset = rightWhitespaceBars(nextPayload.candles.length);
        const chart = createChart(chartHostRef.current, {
          width: nextPayload.width,
          height: nextPayload.height,
          attributionLogo: false,
          layout: {
            background: { type: ColorType.Solid, color: palette.background },
            textColor: palette.text,
          },
          grid: {
            vertLines: { color: palette.grid },
            horzLines: { color: palette.grid },
          },
          timeScale: {
            borderColor: palette.grid,
            timeVisible: true,
            secondsVisible: false,
            rightOffset,
          },
          rightPriceScale: {
            borderColor: palette.grid,
            ticksVisible: true,
            minimumWidth: priceScaleMinimumWidth(priceFormat.precision),
            scaleMargins: { top: 0.08, bottom: 0.08 },
          },
          crosshair: { mode: 0 },
        });
        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
          upColor: palette.up,
          downColor: palette.down,
          borderVisible: false,
          wickUpColor: palette.up,
          wickDownColor: palette.down,
          priceFormat,
        });
        candleSeries.setData(nextPayload.candles.map((c) => ({
          time: c.time,
          open: Number(c.open),
          high: Number(c.high),
          low: Number(c.low),
          close: Number(c.close),
        })));

        let nextPaneIndex = 1;
        const paneMap = new Map();
        const paneIndexFor = (paneKey) => {
          if (!paneKey || paneKey === "price") return 0;
          if (!paneMap.has(paneKey)) {
            paneMap.set(paneKey, nextPaneIndex);
            nextPaneIndex += 1;
          }
          return paneMap.get(paneKey);
        };

        const hasVolume = nextPayload.show_volume && nextPayload.candles.some((c) => Number(c.volume) > 0);
        if (hasVolume) {
          const volumePane = nextPaneIndex;
          nextPaneIndex += 1;
          const volumeSeries = chart.addSeries(HistogramSeries, {
            priceFormat: { type: "volume" },
            priceScaleId: "",
            color: "rgba(148, 163, 184, 0.45)",
          }, volumePane);
          volumeSeries.setData(nextPayload.candles.map((c) => ({
            time: c.time,
            value: Number(c.volume) || 0,
            color: Number(c.close) >= Number(c.open) ? "rgba(34,197,94,0.38)" : "rgba(239,68,68,0.38)",
          })));
        }

        nextPayload.indicator_series.forEach((series) => {
          if (!series?.data?.length) return;
          const pane = paneIndexFor(series.pane);
          const SeriesCtor = series.kind === "histogram" ? HistogramSeries : LineSeries;
          const options = series.kind === "histogram"
            ? {
                color: series.color || "rgba(59,130,246,0.65)",
                priceScaleId: "",
              }
            : {
                color: series.color || "#60A5FA",
                lineWidth: 2,
                lineStyle: series.dashed ? LineStyle.Dashed : LineStyle.Solid,
                priceLineVisible: false,
                lastValueVisible: false,
                ...(pane === 0 ? { priceFormat } : {}),
              };
          const lwSeries = chart.addSeries(SeriesCtor, options, pane);
          lwSeries.setData(series.data.map((point) => ({
            time: point.time,
            value: Number(point.value),
            color: point.color,
          })));
        });

        chart.timeScale().setVisibleLogicalRange({
          from: 0,
          to: Math.max(0, nextPayload.candles.length - 1 + rightOffset),
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.__PAXIA_CHART_READY__ = true;
          });
        });
      } catch (err) {
        const message = err?.message || String(err);
        setError(message);
        window.__PAXIA_CHART_ERROR__ = message;
        window.__PAXIA_CHART_READY__ = false;
      }
    });
  }, [clearChart]);

  useEffect(() => {
    const onPayload = (event) => {
      const nextPayload = event.detail || event.data?.payload || event.data;
      if (!nextPayload || typeof nextPayload !== "object" || !Array.isArray(nextPayload.candles)) return;
      renderPayload(nextPayload);
    };
    window.addEventListener("paxia:chart-snapshot-payload", onPayload);
    window.addEventListener("message", onPayload);

    if (window.__PAXIA_CHART_SNAPSHOT_PAYLOAD__) {
      renderPayload(window.__PAXIA_CHART_SNAPSHOT_PAYLOAD__);
    }

    window.__PAXIA_RENDER_CHART_SNAPSHOT__ = renderPayload;
    window.__PAXIA_CHART_READY__ = false;
    window.__PAXIA_CHART_ERROR__ = "";

    return () => {
      window.removeEventListener("paxia:chart-snapshot-payload", onPayload);
      window.removeEventListener("message", onPayload);
      clearChart();
    };
  }, [clearChart, renderPayload]);

  const palette = payload?.theme === "light" ? LIGHT : DARK;
  const indicatorText = payload?.indicator_names?.filter(Boolean).slice(0, 8).join("  ");

  return (
    <div
      ref={rootRef}
      id="chart-snapshot-root"
      style={{
        width: payload?.width || 800,
        height: payload?.height || 600,
        background: palette.background,
        color: palette.text,
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
      }}
    >
      <div ref={chartHostRef} style={{ width: "100%", height: "100%" }} />
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 12,
          zIndex: 5,
          pointerEvents: "none",
          color: palette.text,
          textShadow: payload?.theme === "light" ? "none" : "0 1px 1px rgba(0,0,0,0.55)",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0 }}>
          {(payload?.symbol || "CHART").toUpperCase()} · {(payload?.timeframe || "").toUpperCase()}
        </div>
        {indicatorText && (
          <div style={{ marginTop: 3, fontSize: 10, color: palette.muted, letterSpacing: 0 }}>
            {indicatorText}
          </div>
        )}
      </div>
      {!payload && !error && (
        <div style={{ position: "absolute", inset: 0, zIndex: 6, display: "grid", placeItems: "center", color: palette.muted, fontSize: 13 }}>
          Waiting for chart payload
        </div>
      )}
      {error && (
        <div style={{ position: "absolute", inset: 0, zIndex: 6, display: "grid", placeItems: "center", color: "#FCA5A5", fontSize: 13 }}>
          {error}
        </div>
      )}
    </div>
  );
}
